'use client';

import React, { useState, useCallback } from 'react';
import { ScreenContent, ScreenTabBar, ScreenFixedTop, ScreenFixedBottom } from '../../screen';
import { ShopSkeleton } from './ShopSkeleton';
import { ErrorMessage } from '../../components/ErrorMessage';
import { EmptyState, Button, ButtonGroup, OverlayBadge } from '../../components/ui';
import { ItemPrice } from '../../components/ItemPrice';
import { ItemListItem } from '../../components/ItemListItem';
import { useNavigation } from '../../contexts/NavigationContext';
import { useLocalTranslation } from '../../hooks/useLocalTranslation';
import { useIsDesktop } from '../../hooks/useIsDesktop';
import { DesktopSidebarPanel } from '../../components/desktop/DesktopSidebarPanel';
import { useShopSkins, type ShopSkinsFilter } from '../../queries/useShopQueries';
import { SkinsFilterModal, DEFAULT_SKINS_FILTERS, type SkinsFilterValues } from './SkinsFilterModal';
import { ShopFiltersPanel, DEFAULT_FILTERS, type FilterValues } from './ShopFiltersPanel';
import type { ShopSkin } from '../../services/generated/Api';
import {
  GetShopSkinsSortBy,
  ShopControllerGetShopItemsParams1SortByEnum,
} from '../../services/generated/Api';
import type { ScreenParams } from '../../types/navigation';
import translations from './ShopScreen.translations';
import { FaSlidersH } from '../../components/icons';
interface ShopSkinsScreenProps {
  params?: ScreenParams & {
    weapon?: string;
    category?: string;
    weaponImage?: string;
  };
  onBack?: () => void;
}

export const ShopSkinsScreen: React.FC<ShopSkinsScreenProps> = ({ params }) => {
  const { t } = useLocalTranslation(translations);
  const navigation = useNavigation();
  const isDesktop = useIsDesktop();

  const weapon = params?.weapon ?? '';
  const category = params?.category ?? '';

  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState<SkinsFilterValues>(DEFAULT_SKINS_FILTERS);

  const queryFilters: ShopSkinsFilter = {
    rarity: filters.rarity || undefined,
    sortBy: filters.sortBy,
  };

  const {
    data: skins = [],
    isLoading,
    error,
    refetch,
  } = useShopSkins(weapon, queryFilters);

  const handleApplyFilters = useCallback((newFilters: SkinsFilterValues) => {
    setFilters(newFilters);
    setIsFilterModalOpen(false);
  }, []);

  const handleSkinClick = useCallback(
    (skin: ShopSkin) => {
      navigation.navigate('shop-items', {
        weapon,
        skinName: skin.skinName,
        category,
        title: skin.skinName,
      });
    },
    [navigation, weapon, category]
  );

  const skinsContent = (
    <>
      {isLoading ? (
        <ShopSkeleton />
      ) : error ? (
        <ErrorMessage error={t('errorLoadSkins')} onRetry={() => refetch()} />
      ) : skins.length === 0 ? (
        <EmptyState title={t('noSkinsFound')} description="" />
      ) : (
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(100%,160px),1fr))] gap-1 pt-2">
          {skins.map((skin) => (
            <ItemListItem
              key={skin.skinName}
              className="flex flex-col cursor-pointer overflow-hidden"
              item={{ name: `${weapon} | ${skin.skinName}`, imageUrl: skin.imageUrl, rarityColor: skin.rarityColor }}
              onClick={() => handleSkinClick(skin)}
              hideCondition
            >
              <OverlayBadge>
                <span className="flex items-center gap-1 text-sm font-semibold text-price">
                  {t('from')} <ItemPrice credits={skin.minPriceInCredits} />
                </span>
              </OverlayBadge>
            </ItemListItem>
          ))}
        </div>
      )}
    </>
  );

  const SKINS_SORT_OPTIONS = [
    ShopControllerGetShopItemsParams1SortByEnum.PriceAsc,
    ShopControllerGetShopItemsParams1SortByEnum.PriceDesc,
  ];

  const SKINS_SORT_TO_PANEL: Record<GetShopSkinsSortBy, ShopControllerGetShopItemsParams1SortByEnum> = {
    [GetShopSkinsSortBy.PriceAsc]: ShopControllerGetShopItemsParams1SortByEnum.PriceAsc,
    [GetShopSkinsSortBy.PriceDesc]: ShopControllerGetShopItemsParams1SortByEnum.PriceDesc,
  };

  const desktopFilters: FilterValues = {
    ...DEFAULT_FILTERS,
    category: category,
    weapon: weapon,
    rarity: filters.rarity,
    sortBy: SKINS_SORT_TO_PANEL[filters.sortBy],
  };

  const handleDesktopFilterApply = useCallback((f: FilterValues) => {
    const sortBy = Object.values(GetShopSkinsSortBy).find((value) => String(value) === f.sortBy);
    setFilters({ rarity: f.rarity, sortBy: sortBy ?? DEFAULT_SKINS_FILTERS.sortBy });
  }, []);

  if (isDesktop) {
    return (
      <ScreenContent scrollKey={`shop-skins-${weapon}`}>
        <div className="flex gap-6 p-6">
          <DesktopSidebarPanel width={260} className="p-5">
            <h2 className="text-sm font-bold text-white m-0 mb-3">{weapon}</h2>
            <ShopFiltersPanel
              filters={desktopFilters}
              sortOptions={SKINS_SORT_OPTIONS}
              onApply={handleDesktopFilterApply}
              onReset={() => {
                navigation.resetStack('games', 'shop');
              }}
            />
          </DesktopSidebarPanel>
          <div className="flex-1 min-w-0">{skinsContent}</div>
        </div>
      </ScreenContent>
    );
  }

  return (
    <>
      <ScreenFixedTop title={weapon} />
      <ScreenTabBar />
      <ScreenFixedBottom>
        <ButtonGroup>
          <Button size="l" before={<FaSlidersH />} onClick={() => setIsFilterModalOpen(true)} stretched>{t('showFilters')}</Button>
        </ButtonGroup>
      </ScreenFixedBottom>
      <ScreenContent scrollKey={`shop-skins-${weapon}`}>{skinsContent}</ScreenContent>
      <SkinsFilterModal isOpen={isFilterModalOpen} filters={filters} onApply={handleApplyFilters} onClose={() => setIsFilterModalOpen(false)} />
    </>
  );
};
